import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@arcadeai/design-system/components/ui/atoms/accordion";
import { Form, FormField } from "@arcadeai/design-system/rhf";
import { McpServerPickerSkeleton } from "@arcadeai/ui-kit/mcp-server-picker";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, SlidersHorizontal } from "lucide-react";
import { Suspense } from "react";
import { useWatch } from "react-hook-form";
import { AllowedPassthroughHeadersField } from "../components/form/allowed-passthrough-headers-field";
import { ConsentSkipClientIdsField } from "../components/form/consent-skip-client-ids-field";
import { DescriptionFormField } from "../components/form/description-form-field";
import { DiscoveryFormField } from "../components/form/discovery-form-field";
import { InstructionsFormField } from "../components/form/instructions-form-field";
import { NameFormField } from "../components/form/name-form-field";
import { SlugFormField } from "../components/form/slug-form-field";
import {
  type GatewayFormController,
  useGatewayCreateForm,
  useGatewayEditForm,
} from "../hooks/use-gateway-form";
import type { McpServer } from "../types";
import { AudienceAuthField } from "./components/audience-auth-field";
import { CreateFooterSummary } from "./components/create-footer-summary";
import { GatewaySubmitButton } from "./components/gateway-submit-button";
import { McpServersField } from "./components/mcp-servers-field";
import { Step } from "./components/step";
import { ToolsetNotice } from "./components/toolset-notice";

/** Create a new gateway. */
export function GatewayCreatePage({
  initialTools,
}: {
  initialTools?: string[];
}) {
  const controller = useGatewayCreateForm({ initialTools });
  return <GatewayFormView controller={controller} />;
}

/** Edit an existing gateway. */
export function GatewayEditPage({ gateway }: { gateway: McpServer }) {
  const controller = useGatewayEditForm({ gateway });
  return <GatewayFormView controller={controller} gateway={gateway} />;
}

function GatewayFormView({
  controller,
  gateway,
}: {
  controller: GatewayFormController;
  /** Present only when editing — supplies the original slug for validation. */
  gateway?: McpServer;
}) {
  const {
    form,
    isEditing,
    isPending,
    organizationId,
    projectId,
    formState,
    selectedApps,
    handlers,
  } = controller;

  const {
    stepState,
    missingRequirements,
    readyToCreate,
    selectedToolCount,
    authType,
  } = formState;

  const toolSearchActive = Boolean(
    useWatch({ control: form.control, name: "tool_filter.discovery.enabled" })
  );

  // Completion checks make the create flow feel like a guided wizard. On edit
  // everything is pre-filled, so they'd all show green instantly and mean
  // nothing — there the markers stay as plain step numbers.
  const stepChecks = isEditing
    ? {
        name: false,
        tools: false,
        audience: false,
        instructions: false,
      }
    : {
        name: stepState.nameComplete,
        tools: stepState.toolsComplete,
        audience: stepState.audienceComplete,
        instructions: stepState.instructionsComplete,
      };

  return (
    <Form {...form}>
      <form
        className="flex h-full flex-col"
        onSubmit={form.handleSubmit(handlers.onSubmit, handlers.onInvalid)}
      >
        <FormField
          control={form.control}
          name="status"
          render={({ field }) => <input type="hidden" {...field} />}
        />

        <div className="flex min-h-0 w-full flex-1 flex-col gap-8 overflow-y-auto pr-4 pb-8">
          <div className="flex items-center gap-3">
            <Link
              aria-label="Back to MCP gateways"
              className="inline-flex shrink-0 items-center text-muted-foreground transition-colors hover:text-foreground"
              params={{ organizationId, projectId }}
              to="/orgs/$organizationId/projects/$projectId/mcp-gateways"
            >
              <ArrowLeft aria-hidden className="size-5" />
            </Link>
            <h1 className="font-bold text-xl">
              {isEditing ? "Edit MCP gateway" : "Create MCP gateway"}
            </h1>
          </div>

          <Step
            complete={stepChecks.name}
            description="Give it a name you'll recognize later."
            divider={false}
            headingId="create-name-heading"
            index={1}
            title="Name it"
          >
            <div className="max-w-xl">
              <NameFormField autoFocus control={form.control} hideDescription />
            </div>
          </Step>

          <Step
            complete={stepChecks.tools}
            description="Choose the tools your agent can use to get work done."
            headingId="create-tools-heading"
            index={2}
            title="Select apps"
          >
            <div className="space-y-4">
              <DiscoveryFormField control={form.control} />
              <Suspense
                fallback={
                  <div className="h-[19rem] xl:h-[22rem]">
                    <McpServerPickerSkeleton />
                  </div>
                }
              >
                <McpServersField control={form.control} />
              </Suspense>
              <ToolsetNotice
                count={selectedToolCount}
                toolSearchActive={toolSearchActive}
              />
            </div>
          </Step>

          <Step
            complete={stepChecks.audience}
            description="Decide how people sign in when they connect."
            headingId="create-audience-label"
            index={3}
            title="Choose who connects"
          >
            <AudienceAuthField control={form.control} />
          </Step>

          <Step
            complete={stepChecks.instructions}
            description="Tell your agent when and why to use these tools."
            headingId="create-instructions-heading"
            index={4}
            optional
            title="Guide your agent"
          >
            <div className="max-w-2xl">
              <InstructionsFormField
                control={form.control}
                hideDescription
                hideLabel
              />
            </div>
          </Step>

          <Accordion collapsible type="single">
            <AccordionItem
              className="border-border border-t pt-2"
              value="advanced"
            >
              <AccordionTrigger className="rounded-md px-3 py-2.5 font-medium text-foreground text-sm transition-colors hover:bg-muted/40 hover:no-underline focus-visible:bg-muted/40 data-[state=open]:bg-muted/20">
                <span className="flex items-center gap-2">
                  <SlidersHorizontal
                    aria-hidden
                    className="size-4 text-muted-foreground"
                  />
                  Advanced settings
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pt-4 pb-2">
                <div className="grid grid-cols-1 gap-x-12 gap-y-6 md:grid-cols-2">
                  <div className="space-y-6">
                    <DescriptionFormField control={form.control} />
                    <SlugFormField
                      control={form.control}
                      originalSlug={gateway?.slug}
                    />
                  </div>
                  <div className="space-y-6">
                    <AllowedPassthroughHeadersField control={form.control} />
                    <ConsentSkipClientIdsField control={form.control} />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="flex shrink-0 flex-col gap-3 border-border border-t bg-background pt-4 pr-4 sm:flex-row sm:items-center sm:justify-between">
          <CreateFooterSummary
            authType={authType}
            isEditing={isEditing}
            missingRequirements={missingRequirements}
            readyToCreate={readyToCreate}
            selectedApps={selectedApps}
            selectedToolCount={selectedToolCount}
          />
          <GatewaySubmitButton
            isEditing={isEditing}
            isPending={isPending}
            missingRequirements={missingRequirements}
            readyToCreate={readyToCreate}
          />
        </div>
      </form>
    </Form>
  );
}
